import Navigation from "@/components/Navigation";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TrendingUp, Users, DollarSign, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";

const caseStudies = [
  {
    name: "Sarah Martinez",
    role: "E-commerce Entrepreneur",
    image: "SM",
    challenge: "Sarah was working a 9-5 job and wanted to start an online business but had no idea where to begin. She'd tried multiple side hustles that all failed.",
    solution: "Through Launch To Revenue, Sarah identified a profitable niche in sustainable fashion accessories. She implemented our proven marketing framework and built her first sales funnel in week 3.",
    results: [
      { icon: DollarSign, label: "Revenue", value: "$15K/month" },
      { icon: Clock, label: "Time to Launch", value: "60 days" },
      { icon: Users, label: "Customer Base", value: "1,200+" },
      { icon: TrendingUp, label: "Growth Rate", value: "35%/month" }
    ],
    quote: "This course gave me the exact roadmap I needed. No fluff, just actionable strategies that actually work. I quit my job after 6 months!",
    timeline: [
      { week: "Week 1-2", milestone: "Identified niche and validated market demand" },
      { week: "Week 3-4", milestone: "Created first product line and sales funnel" },
      { week: "Week 5-6", milestone: "Generated first $1K in revenue" },
      { week: "Week 7-12", milestone: "Scaled to $15K/month with paid ads" }
    ]
  },
  {
    name: "James Chen",
    role: "Marketing Consultant",
    image: "JC",
    challenge: "James was a freelance marketer struggling to find consistent clients. His income was unpredictable and he was stuck doing low-paying projects.",
    solution: "He used our positioning and offer creation framework to transform his services into high-ticket consulting packages. He also implemented our client acquisition system.",
    results: [
      { icon: DollarSign, label: "Revenue", value: "$45K/month" },
      { icon: Clock, label: "Time to Scale", value: "90 days" },
      { icon: Users, label: "Clients", value: "12 retainers" },
      { icon: TrendingUp, label: "Price Increase", value: "300%" }
    ],
    quote: "I went from charging $500/project to $5K/month retainers. The course taught me how to position myself as an authority and attract premium clients.",
    timeline: [
      { week: "Week 1-2", milestone: "Repositioned services and created premium offers" },
      { week: "Week 3-4", milestone: "Signed first $5K/month client" },
      { week: "Week 5-8", milestone: "Built automated lead generation system" },
      { week: "Week 9-12", milestone: "Achieved consistent $45K/month revenue" }
    ]
  },
  {
    name: "Emily Rodriguez",
    role: "Course Creator",
    image: "ER",
    challenge: "Emily had expertise in graphic design but couldn't figure out how to package and sell her knowledge online. She'd tried launching a course that completely flopped.",
    solution: "She learned our validated course creation process and launched a mini-product first to test the market. Then scaled to a full course using our proven launch formula.",
    results: [
      { icon: DollarSign, label: "Launch Revenue", value: "$28K" },
      { icon: Clock, label: "Launch Time", value: "30 days" },
      { icon: Users, label: "Students", value: "450+" },
      { icon: TrendingUp, label: "Repeat Rate", value: "65%" }
    ],
    quote: "The Launch To Revenue framework helped me avoid all the mistakes I made before. I validated my idea first, then launched with confidence and hit my revenue goals.",
    timeline: [
      { week: "Week 1-2", milestone: "Validated course idea with mini-product ($2K revenue)" },
      { week: "Week 3-4", milestone: "Created course curriculum and content" },
      { week: "Week 5-6", milestone: "Built launch funnel and email sequence" },
      { week: "Week 7-8", milestone: "Launched course with $28K in sales" }
    ]
  }
];

const CaseStudies = () => {
  const navigate = useNavigate();

  return (
    <>
      <Navigation />
      <main className="min-h-screen pt-16">
      {/* Header */}
      <section className="py-24 px-4 bg-gradient-to-br from-background via-primary/5 to-background">
        <div className="container mx-auto max-w-4xl text-center space-y-6">
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight">
            Real Success Stories
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground">
            See how entrepreneurs transformed their businesses with Launch To Revenue
          </p>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-24 px-4">
        <div className="container mx-auto max-w-6xl space-y-32">
          {caseStudies.map((study, index) => (
            <div key={index} className="space-y-12">
              {/* Profile Header */}
              <div className="flex items-center gap-6">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-2xl font-bold text-primary-foreground">
                  {study.image}
                </div>
                <div>
                  <h2 className="text-3xl font-bold">{study.name}</h2>
                  <p className="text-lg text-muted-foreground">{study.role}</p>
                </div>
              </div>

              {/* Challenge & Solution */}
              <div className="grid md:grid-cols-2 gap-8">
                <Card className="border-2">
                  <CardContent className="p-6 space-y-4">
                    <h3 className="text-xl font-bold text-destructive">The Challenge</h3>
                    <p className="text-muted-foreground leading-relaxed">{study.challenge}</p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-primary/30">
                  <CardContent className="p-6 space-y-4">
                    <h3 className="text-xl font-bold text-primary">The Solution</h3>
                    <p className="text-muted-foreground leading-relaxed">{study.solution}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Results */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {study.results.map((result, idx) => (
                  <Card key={idx} className="border-2 hover:border-primary/30 transition-all">
                    <CardContent className="p-6 text-center space-y-3">
                      <result.icon className="w-8 h-8 mx-auto text-primary" />
                      <div className="text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                        {result.value}
                      </div>
                      <div className="text-sm text-muted-foreground">{result.label}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Quote */}
              <Card className="bg-primary/5 border-2 border-primary/20">
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed italic">
                    "{study.quote}"
                  </p>
                  <p className="text-sm text-muted-foreground mt-4">— {study.name}</p>
                </CardContent>
              </Card>

              {/* Timeline */}
              <div className="space-y-4">
                <h3 className="text-2xl font-bold">Journey Timeline</h3>
                <div className="space-y-4">
                  {study.timeline.map((item, idx) => (
                    <div key={idx} className="flex gap-4 items-start">
                      <div className="flex-shrink-0 w-32 font-semibold text-primary">
                        {item.week}
                      </div>
                      <div className="flex-grow border-l-2 border-primary pl-6 pb-6">
                        <p className="text-muted-foreground">{item.milestone}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {index < caseStudies.length - 1 && (
                <div className="border-t-2 border-border" />
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 bg-gradient-to-br from-primary/10 via-background to-accent/10">
        <div className="container mx-auto max-w-4xl text-center space-y-8">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight">
            Ready to Write Your Success Story?
          </h2>
          <p className="text-xl text-muted-foreground">
            Join hundreds of entrepreneurs transforming their businesses
          </p>
          <Button 
            size="lg" 
            className="text-lg px-8 py-6 h-auto"
            onClick={() => navigate("/auth")}
          >
            Get Started Today
          </Button>
        </div>
      </section>
    </main>
    </>
  );
};

export default CaseStudies;
